
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import { useEffect } from "react";
import { FORM_MAX_WIDTH, propertyOwnershipOptions } from "../../../utils/constants";
import { RoundedBackgroundIcon } from "../../../components/roundedBackgroundIcon";
import MapOutlinedIcon from '@material-ui/icons/MapOutlined';
import {
  createDateField,
  createRadioField,
} from '@roc/ui';
import { FULL_RECOURSE, insertIf, loanRecourse, PURCHASE, REFINANCE, yesNoOptions, yesNoOptionsBoolean } from "@roc/feature-utils";
import { observer } from "mobx-react";
import { PortfolioInformationFormStore } from "../../../stores/portfolioInformation/portfolioInformationFormStore";
import { PropertyInformationTimeline, TimelineIconAnnualHoaDuesValue, TimelineIconAnnualInsuranceValue, TimelineIconAnnualTaxesValue, TimelineIconMarketValue, TimelineIconMonthlyMarketRentValue, TimelineIconPurchasePrice, TimelineIconRenoSpent, TimelineIconTotalEstimatedAsIsValue } from "../../propertyInformation/components/propertyInformationTimeline";
import { PurchasePrice } from "../components/purchasePrice";
import { LowestPropertyAsIsValue } from "../components/lowestPropertyAsIsValue";
import { HighestPropertyAsIsValue } from "../components/highestPropertyAsIsValue";
import { CapitalImprovements } from "../components/capitalImprovements";
import { TotalPropertyAsIsValue } from "../components/totalPropertyAsIsValue";
import { MonthlyMarketRent } from "../components/monthlyMarketRent";
import { AnnualHoaDues } from "../components/annualHoaDues";
import { AnnualTaxes } from "../components/annualTaxes";
import { AnnualInsurance } from "../components/annualInsurance";
import { ItemDisplay } from "../../../components/itemDisplay";
import { formatCurrency } from "@roc/ui/utils";
import BusinessCenterOutlinedIcon from '@material-ui/icons/BusinessCenterOutlined';
import {
  createCurrencyField,
} from '../../../components/temp/componentBuiderTemp';
import { Section8, SeekingCashOut } from "../../propertyInformation/components/propertyInformationFields";

const sfrPortfolioPropertyManagement = [
  { value: 'Self Managed', label: 'Self Managed' },
  { value: 'Third Party Management', label: 'Third Party Managed' },
];


const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    position: 'relative',
    maxWidth: 1300,
    margin: 'auto',
  },
  substractionContainer: {
    display: 'flex',
    width: '90%',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: '20px'
  },
  symbols: {
    color: '#BABEC5',
    fontSize: '60px',
    fontWeight: 300
  },
}));

interface PortfolioDetailsFormProps {
  portfolioInformationFormStore: PortfolioInformationFormStore;
}

export const PortfolioDetailsForm = observer(({ portfolioInformationFormStore }: PortfolioDetailsFormProps) => {
  const classes = useStyles();

  const loanPurpose = portfolioInformationFormStore.form.fields.loanPurpose.value;

  const isPurchase = loanPurpose !== REFINANCE;

  const anyDebt = portfolioInformationFormStore.form.fields.anyDebt.value;

  const anyDebtChangeHandler = (value) => {
    portfolioInformationFormStore.onFieldChange("anyDebt", value === "true");

    if (value !== "true") {
      portfolioInformationFormStore.onFieldChange("totalDebtPayoff", null);
    }
  }

  useEffect(() => {
    if (portfolioInformationFormStore.form.fields.loanRecourse.value !== FULL_RECOURSE) {
      portfolioInformationFormStore.onFieldChange('loanRecourse', FULL_RECOURSE);
    }
  }, [portfolioInformationFormStore]);

  return (
    <Grid
      container
      spacing={2}
      className={classes.container}
      style={{ maxWidth: FORM_MAX_WIDTH, marginBottom: '0.5rem' }}
    >
      <Grid item xs={12}>
        <Grid
          container
          spacing={2}
          justifyContent="flex-start"
          alignItems="center"
        >
          <Grid item>
            <RoundedBackgroundIcon
              Icon={BusinessCenterOutlinedIcon}
              iconColor="#6D6D6D"
            />
          </Grid>
          <Grid item>
            <Typography variant="h4" style={{ color: '#6D6D6D' }}>
              Portfolio Details
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        {createRadioField({
          label: 'Is the property owned by the borrower(s)?',
          fieldName: 'loanPurpose',
          testId: 'loanPurpose',
          store: portfolioInformationFormStore,
          options: propertyOwnershipOptions,
          row: true,
          standaloneLabel: false
        })}
      </Grid>
      <Grid item xs={12} sm={6}>
        <Section8 store={portfolioInformationFormStore}></Section8>
      </Grid>
      <Grid item xs={6}>
        {!isPurchase && createDateField({
          label: 'Last Purchase Date',
          fieldName: 'lastPurchaseDate',
          testId: 'lastPurchaseDate',
          store: portfolioInformationFormStore,
          standaloneLabel: false
        })}
      </Grid>
      {!isPurchase && (
        <>
          <Grid item xs={6}>
            {createRadioField({
              label: 'Any Debt on the portfolio?',
              fieldName: 'anyDebt',
              testId: 'anyDebt',
              store: portfolioInformationFormStore,
              options: yesNoOptionsBoolean,
              row: true,
              standaloneLabel: false,
              onChange: anyDebtChangeHandler
            })}
          </Grid>
          <Grid item xs={6}>
            {anyDebt && createCurrencyField({
              label: "Current Debt on the portfolio",
              fieldName: 'totalDebtPayoff',
              testId: 'totalDebtPayoff',
              store: portfolioInformationFormStore,
              standaloneLabel: false,
              isRequired: true
            })}
          </Grid>
        </>)}
      {anyDebt && !isPurchase && (
        <>
          <Grid item xs={6}>
            <SeekingCashOut store={portfolioInformationFormStore} />
          </Grid>
        </>)}
      <Grid item xs={12}>
        <PropertyInformationTimeline
          items={[
            {
              icon: <TimelineIconPurchasePrice />,
              right: isPurchase && (
                <PurchasePrice label="What is the Total Purchase Price" store={portfolioInformationFormStore} />
              ),
              left: !isPurchase && (
                <PurchasePrice label="What was the Total Purchase Price" store={portfolioInformationFormStore} />
              )
            },
            {
              icon: <TimelineIconMarketValue />,
              right: <HighestPropertyAsIsValue store={portfolioInformationFormStore} />,
              left: <LowestPropertyAsIsValue store={portfolioInformationFormStore} />
            },
            ...insertIf(!isPurchase, [
              {
                icon: <TimelineIconRenoSpent />,
                left: <CapitalImprovements store={portfolioInformationFormStore} />,
              },
            ]),
            {
              icon: <TimelineIconTotalEstimatedAsIsValue />,
              right: <TotalPropertyAsIsValue store={portfolioInformationFormStore} />,
            },
            {
              icon: <TimelineIconMonthlyMarketRentValue />,
              right: <MonthlyMarketRent store={portfolioInformationFormStore} />,
              gray: true,
            },
            {
              icon: <TimelineIconAnnualTaxesValue />,
              right: <AnnualTaxes store={portfolioInformationFormStore} />,
              gray: true,
            },
            {
              icon: <TimelineIconAnnualInsuranceValue />,
              right: <AnnualInsurance store={portfolioInformationFormStore} />,
              gray: true,
            },
            {
              icon: <TimelineIconAnnualHoaDuesValue />,
              right: <AnnualHoaDues store={portfolioInformationFormStore} />,
              gray: true,
            },

          ]}
        />
      </Grid>
      <Grid item xs={12}>
        <Box className={classes.substractionContainer}>
          <ItemDisplay bottomLabel label="Total Annual Income" value={`${formatCurrency(portfolioInformationFormStore.totalAnnualIncome)}`}></ItemDisplay>
          <Typography className={classes.symbols} align="center" variant="h4">
            -
          </Typography>
          <ItemDisplay bottomLabel label="Annual Expenses" value={`${formatCurrency(portfolioInformationFormStore.annualExpenses)}`}></ItemDisplay>
          <Typography className={classes.symbols} align="center" variant="h4">
            =
          </Typography>
          <ItemDisplay bottomLabel label="Annual NOI" value={`${formatCurrency(portfolioInformationFormStore.annualNOI)}`}></ItemDisplay>
        </Box>
      </Grid>
    </Grid>
  );
});
